import React, { useState, useEffect } from "react";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Users, CreditCard, BookOpen, TrendingUp, Search, ChevronLeft, ChevronRight, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/admin/stats`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setStats)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      try {
        let url = `${API}/admin/members?page=${page}&limit=15`;
        if (statusFilter) url += `&status=${statusFilter}`;
        if (programFilter) url += `&program=${programFilter}`;
        const res = await fetch(url, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setMembers(data.members);
          setTotal(data.total);
        }
      } catch {}
      setLoading(false);
    };
    loadMembers();
  }, [page, statusFilter, programFilter]);

  const handleUpdateMember = async (userId, field, value) => {
    try {
      const res = await fetch(`${API}/admin/members/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Miembro actualizado");
        setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, [field]: value } : m));
      }
    } catch {
      toast.error("Error actualizando");
    }
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-6 h-6 text-[#20D3C0]" />
          <h1 className="text-3xl font-bold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>Panel de Administracion</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Users, label: "Total usuarios", value: stats.total_users, color: "#193B6E" },
              { icon: TrendingUp, label: "Miembros activos", value: stats.active_members, color: "#20D3C0" },
              { icon: CreditCard, label: "Pagos realizados", value: stats.paid_orders, color: "#22C55E" },
              { icon: BookOpen, label: "Quizzes hechos", value: stats.total_quiz_attempts, color: "#F59E0B" },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-testid={`admin-stat-${i}`}>
                <stat.icon className="w-5 h-5 mb-2" style={{ color: stat.color }} />
                <p className="text-2xl font-bold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
                <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Program stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <h3 className="font-semibold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }}>Exacer-COLBACH</h3>
              <p className="text-3xl font-bold text-[#20D3C0]">{stats.exacer_enrolled}</p>
              <p className="text-xs text-slate-500">inscritos</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <h3 className="font-semibold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }}>Acredita-Bach</h3>
              <p className="text-3xl font-bold text-[#20D3C0]">{stats.acredita_enrolled}</p>
              <p className="text-xs text-slate-500">inscritos</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="Filtrar por estatus" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="free">Gratuito</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select value={programFilter} onValueChange={v => { setProgramFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger data-testid="filter-program">
                <SelectValue placeholder="Filtrar por programa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="exacer">Exacer-COLBACH</SelectItem>
                <SelectItem value="acredita">Acredita-Bach</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-slate-500">{total} resultados</p>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="members-table">
              <thead className="bg-[#F8FAFC] border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Nombre</th>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Email</th>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Programa</th>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Estatus</th>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Rol</th>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Registro</th>
                  <th className="text-left text-xs font-semibold text-slate-500 px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((member) => (
                  <tr key={member.user_id} className="hover:bg-[#F8FAFC] transition-colors" data-testid={`member-row-${member.user_id}`}>
                    <td className="px-4 py-3 text-sm text-[#193B6E] font-medium">{member.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{member.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{member.program || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        member.subscription_status === 'active' ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {member.subscription_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{member.role}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString('es-MX') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {member.subscription_status !== 'active' && (
                          <button
                            onClick={() => handleUpdateMember(member.user_id, 'subscription_status', 'active')}
                            className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-md hover:bg-green-100 transition-colors"
                            data-testid={`activate-${member.user_id}`}
                          >
                            Activar
                          </button>
                        )}
                        {member.subscription_status === 'active' && (
                          <button
                            onClick={() => handleUpdateMember(member.user_id, 'subscription_status', 'free')}
                            className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-md hover:bg-red-100 transition-colors"
                            data-testid={`deactivate-${member.user_id}`}
                          >
                            Desactivar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 text-sm text-[#193B6E] disabled:opacity-30"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" /> Anterior
              </button>
              <span className="text-sm text-slate-500">Pagina {page} de {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 text-sm text-[#193B6E] disabled:opacity-30"
                data-testid="next-page"
              >
                Siguiente <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
